import React, { useState } from 'react';
import { Mail, MapPin, Clock, Send, MessageSquare, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { companyInfo } from '../data/mock';

const Contact = () => {
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus(null);

    try {
      const response = await fetch('http://localhost:3000/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          company: form.company,
          phone: form.phone,
          subject: form.subject,
          message: form.message,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send message.');
      }

      setStatus({ type: 'success', message: 'Pesan berhasil dikirim! Kami akan menghubungi Anda segera.' });
      setForm({ firstName: '', lastName: '', email: '', company: '', phone: '', subject: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Gagal mengirim pesan. Coba lagi nanti.' });
    } finally {
      setSubmitting(false);
    }
  };

  const contactDetails = [
    {
      icon: Phone,
      title: 'Phone',
      value: companyInfo.phone,
      href: `tel:${companyInfo.phone}`,
      description: 'Call us anytime'
    },
    {
      icon: Mail,
      title: 'Email',
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`,
      description: 'Send us an email anytime'
    },
    {
      icon: MapPin,
      title: 'Address',
      value: companyInfo.address,
      href: '#',
      description: 'Visit our office'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      value: companyInfo.workingHours,
      href: null,
      description: 'We\'re available'
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 via-slate-100/50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Contact <span className="text-slate-700">Us</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Have questions about our products or services? We're here to help. Reach out to us and our team will get back to you promptly.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Details */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactDetails.map((item, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-6 hover:bg-slate-100 transition-colors group">
                <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-xs text-slate-500 mb-3">{item.description}</p>
                {item.href ? (
                  <a href={item.href} className="text-slate-700 font-medium text-sm hover:text-slate-900 break-words">
                    {item.value}
                  </a>
                ) : (
                  <p className="text-slate-700 font-medium text-sm">{item.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info Card */}
            <div>
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-10 text-white h-full">
                <div className="flex items-center gap-3 mb-8">
                  <MessageSquare className="w-8 h-8" />
                  <h2 className="text-2xl font-bold">Get in Touch</h2>
                </div>
                
                <p className="text-slate-300 mb-10 leading-relaxed">
                  Whether you're looking to place an order, request a quote, or simply have a question about our services, we're here to assist you.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-slate-300 text-sm">Call us at</p>
                      <a href={`tel:${companyInfo.phone}`} className="font-medium hover:text-white transition-colors">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-slate-300 text-sm">Email us at</p>
                      <a href={`mailto:${companyInfo.email}`} className="font-medium hover:text-white transition-colors">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-slate-300 text-sm">Visit us at</p>
                      <p className="font-medium">{companyInfo.address}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 pt-8 border-t border-white/20">
                  <p className="text-slate-300 text-sm mb-2">Business Hours</p>
                  <p className="font-medium">{companyInfo.workingHours}</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-10 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Send Us a Message</h2>
              <p className="text-slate-600 mb-8">Fill out the form below and we'll respond within 24 hours.</p>

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">First Name</label>
                    <Input name="firstName" placeholder="John" value={form.firstName} onChange={handleInputChange} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Last Name</label>
                    <Input name="lastName" placeholder="Doe" value={form.lastName} onChange={handleInputChange} required />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                  <Input name="email" type="email" placeholder="john@company.com" value={form.email} onChange={handleInputChange} required />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
                  <Input name="company" placeholder="Your Company Ltd." value={form.company} onChange={handleInputChange} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                  <Input name="phone" type="tel" placeholder="(123) 456-7890" value={form.phone} onChange={handleInputChange} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                  <Input name="subject" placeholder="How can we help you?" value={form.subject} onChange={handleInputChange} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                  <Textarea
                    name="message"
                    placeholder="Tell us about your requirements..."
                    rows={4}
                    className="resize-none"
                    value={form.message}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <Button className="w-full bg-slate-900 hover:bg-slate-700 gap-2" size="lg" type="submit" disabled={submitting}>
                  <Send className="w-4 h-4" />
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>

                {status && (
                  <p className={`text-sm text-center ${status.type === 'success' ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {status.message}
                  </p>
                )}

                <p className="text-xs text-slate-500 text-center">
                  By submitting this form, you agree to our privacy policy.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
